import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Avatar,
    Grid,
    Button,
    Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axios from '../config/axiosConfig';

const HedgeFundDetail = () => {
    const theme = useTheme();
    const { cik } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hedgeFund, setHedgeFund] = useState(null);

    useEffect(() => {
        if (cik) {
            fetchHedgeFundData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cik]);

    const fetchHedgeFundData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/hedge-fund/${cik}`);
            setHedgeFund(response.data.data);
            setError(null);
        } catch (error) {
            console.error('Error fetching hedge fund data:', error);
            setError('Failed to fetch hedge fund data');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'N/A';
        if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
        return `$${value.toString()}`;
    };

    const formatFundName = (name) => {
        if (!name) return 'Unknown Fund';
        return name.toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Legendary': '#FFD700',
            'Index Fund': '#4CAF50',
            'Growth': '#FF9800',
            'Tech': '#2196F3',
            'Activist': '#F44336',
            'Quant': '#9C27B0',
            'Value': '#795548',
            'Multi-Strategy': '#607D8B',
            'Macro': '#3F51B5',
            'ESG': '#8BC34A',
            'Event-Driven': '#E91E63'
        };
        return colors[category] || '#757575';
    };

    const columns = [
        {
            field: 'rank',
            headerName: 'Rank',
            align: 'left',
            headerAlign: 'left',
            minWidth: 100,
            flex: 0.5,
        },
        {
            field: 'symbol',
            headerName: 'Stock',
            minWidth: 150,
            flex: 1.5,
            renderCell: (params) => (
                <Box>
                    <RouterLink
                        to={`/asset/${params.value}`}
                        style={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {params.value}
                    </RouterLink>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            mt: 0.5
                        }}
                    >
                        {params.row.securityName}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'shares',
            headerName: 'Shares',
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            minWidth: 120,
            flex: 1,
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return params.value.toLocaleString();
            }
        },
        {
            field: 'marketValue',
            headerName: 'Market Value',
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            minWidth: 150,
            flex: 1,
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return formatCurrency(params.value);
            }
        },
        {
            field: 'weight',
            headerName: 'Weight',
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            minWidth: 100,
            flex: 0.75,
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return `${params.value.toFixed(2)}%`;
            }
        },
        {
            field: 'type',
            headerName: 'Type',
            align: 'center',
            headerAlign: 'center',
            minWidth: 90,
            flex: 0.75,
            renderCell: (params) => (
                <Chip
                    label={params.value === 'stocks' ? 'Stock' : params.value === 'etf' ? 'ETF' : 'Other'}
                    size="small"
                    color={params.value === 'stocks' ? 'primary' : params.value === 'etf' ? 'secondary' : 'default'}
                    variant="outlined"
                />
            )
        },
    ];

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl">
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!hedgeFund) {
        return (
            <Container maxWidth="xl">
                <Alert severity="info" sx={{ mt: 2 }}>
                    No data found for this hedge fund.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ my: 4 }}>
                <Button
                    component={RouterLink}
                    to="/hedge-funds"
                    startIcon={<ArrowBack />}
                    sx={{ mb: 3 }}
                    variant="outlined"
                >
                    Back to Hedge Funds
                </Button>

                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'center', md: 'flex-start' },
                    justifyContent: 'space-between',
                    mb: 4,
                    p: { xs: 2, md: 4 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    position: 'relative'
                }}>
                    <Chip
                        label={hedgeFund.category || 'Hedge Fund'}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            bgcolor: getCategoryColor(hedgeFund.category),
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />

                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        gap: 3,
                        mb: { xs: 3, md: 0 }
                    }}>
                        <Avatar
                            sx={{
                                width: { xs: 80, md: 100 },
                                height: { xs: 80, md: 100 },
                                background: `linear-gradient(135deg, ${getCategoryColor(hedgeFund.category)}, ${getCategoryColor(hedgeFund.category)}88)`
                            }}
                        >
                            <Typography variant="h3" color="white" fontWeight="bold">
                                {hedgeFund.name.charAt(0)}
                            </Typography>
                        </Avatar>
                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant={{ xs: 'h5', sm: 'h4' }} gutterBottom fontWeight="bold">
                                {formatFundName(hedgeFund.name)}
                            </Typography>
                            {hedgeFund.manager && (
                                <Typography variant="h6" color="primary" gutterBottom>
                                    {hedgeFund.manager}
                                </Typography>
                            )}
                            <Typography variant="subtitle1" color="text.secondary">
                                CIK: {hedgeFund.cik} | Last Updated: {hedgeFund.dataYear ? `${hedgeFund.dataYear} Q${hedgeFund.dataQuarter}` : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 2, mb: 2 }}>
                    Portfolio Fundamentals
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="h4" color="primary" fontWeight="bold">
                                {formatCurrency(hedgeFund.marketValue)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Assets Under Management
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="h4" color="primary" fontWeight="bold">
                                {hedgeFund.numberOfHoldings || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Number of Holdings
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="h4" color="primary" fontWeight="bold">
                                {hedgeFund.averageHoldingPeriod || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Avg. Holding Period (months)
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="h4" color="primary" fontWeight="bold">
                                {typeof hedgeFund.turnover === 'number' ? (hedgeFund.turnover * 100).toFixed(1) + '%' : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Portfolio Turnover
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 2, mb: 2 }}>
                    Absolute Performance
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performancePercentage === 'number'
                                        ? (hedgeFund.performancePercentage >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performancePercentage === 'number'
                                    ? `${hedgeFund.performancePercentage >= 0 ? '+' : ''}${hedgeFund.performancePercentage.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Current Quarter
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performancePercentage1year === 'number'
                                        ? (hedgeFund.performancePercentage1year >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performancePercentage1year === 'number'
                                    ? `${hedgeFund.performancePercentage1year >= 0 ? '+' : ''}${hedgeFund.performancePercentage1year.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                1-Year Return
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performancePercentage3year === 'number'
                                        ? (hedgeFund.performancePercentage3year >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performancePercentage3year === 'number'
                                    ? `${hedgeFund.performancePercentage3year >= 0 ? '+' : ''}${hedgeFund.performancePercentage3year.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                3-Year Return
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performancePercentage5year === 'number'
                                        ? (hedgeFund.performancePercentage5year >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performancePercentage5year === 'number'
                                    ? `${hedgeFund.performancePercentage5year >= 0 ? '+' : ''}${hedgeFund.performancePercentage5year.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                5-Year Return
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 2, mb: 2 }}>
                    Performance vs S&P 500 <Typography component="span" variant="body2" color="text.secondary">(Relative Performance)</Typography>
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performanceRelativeToSP500Percentage === 'number'
                                        ? (hedgeFund.performanceRelativeToSP500Percentage >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performanceRelativeToSP500Percentage === 'number'
                                    ? `${hedgeFund.performanceRelativeToSP500Percentage >= 0 ? '+' : ''}${hedgeFund.performanceRelativeToSP500Percentage.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Current Quarter vs S&P 500
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performance1yearRelativeToSP500Percentage === 'number'
                                        ? (hedgeFund.performance1yearRelativeToSP500Percentage >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performance1yearRelativeToSP500Percentage === 'number'
                                    ? `${hedgeFund.performance1yearRelativeToSP500Percentage >= 0 ? '+' : ''}${hedgeFund.performance1yearRelativeToSP500Percentage.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                1-Year vs S&P 500
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performance3yearRelativeToSP500Percentage === 'number'
                                        ? (hedgeFund.performance3yearRelativeToSP500Percentage >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performance3yearRelativeToSP500Percentage === 'number'
                                    ? `${hedgeFund.performance3yearRelativeToSP500Percentage >= 0 ? '+' : ''}${hedgeFund.performance3yearRelativeToSP500Percentage.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                3-Year vs S&P 500
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={
                                    typeof hedgeFund.performance5yearRelativeToSP500Percentage === 'number'
                                        ? (hedgeFund.performance5yearRelativeToSP500Percentage >= 0 ? 'success.main' : 'error.main')
                                        : 'text.secondary'
                                }
                            >
                                {typeof hedgeFund.performance5yearRelativeToSP500Percentage === 'number'
                                    ? `${hedgeFund.performance5yearRelativeToSP500Percentage >= 0 ? '+' : ''}${hedgeFund.performance5yearRelativeToSP500Percentage.toFixed(1)}%`
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                5-Year vs S&P 500
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2 }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ p: 1 }}>
                        Portfolio Holdings
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ width: '100%' }}>
                        <DataGrid
                            rows={hedgeFund.holdings || []}
                            columns={columns}
                            disableColumnFilter
                            disableColumnMenu
                            disableRowSelectionOnClick
                            autoHeight
                            getRowId={(row) => `${row.symbol}_${row.rank}`}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                                sorting: {
                                    sortModel: [{ field: 'rank', sort: 'asc' }],
                                },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Container>
    );
};

export default HedgeFundDetail;